import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { btnPrimary } from '@/components/rm'
import { BrandMark } from '@/components/brand'
import { LanguageMenu, useT } from '@/i18n'

export const Route = createFileRoute('/reset-password')({ component: ResetPasswordPage })

const inputCls =
  'h-10 w-full rounded-[10px] border border-token-subtle px-3 text-[14px] text-token-primary outline-none transition-colors focus:border-token-strong focus:ring-2 focus:ring-brand/10'

function ResetPasswordPage() {
  const { t } = useT()
  const navigate = useNavigate()
  const [ready, setReady] = useState(false)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  // Supabase parses the recovery link and fires PASSWORD_RECOVERY; also cover an existing session.
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { if (data.session) setReady(true) })
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') setReady(true)
    })
    return () => sub.subscription.unsubscribe()
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (password.length < 8) return setError(t('newPassword.tooShort'))
    if (password !== confirm) return setError(t('newPassword.mismatch'))
    setBusy(true)
    const { error: err } = await supabase.auth.updateUser({ password })
    setBusy(false)
    if (err) return setError(err.message)
    setDone(true)
    setTimeout(() => navigate({ to: '/dashboard' }), 1200)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas-subtle px-4">
      <div className="w-full max-w-sm">
        <div className="mb-7 text-center">
          <div className="mb-4 flex justify-center"><LanguageMenu /></div>
          <BrandMark className="mx-auto mb-3 h-11 w-11" />
          <h1 className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">{t('newPassword.title')}</h1>
        </div>
        <form onSubmit={submit} className="space-y-4 rounded-[16px] border border-token-subtle bg-token-surface p-6">
          {!ready && !done && (
            <p className="text-[13px] text-token-secondary">{t('newPassword.openFromEmail')}</p>
          )}
          {ready && !done && (
            <>
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-token-secondary">{t('newPassword.new')}</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" className={inputCls} placeholder={t('common.passwordRule')} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-token-secondary">{t('newPassword.confirm')}</label>
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" className={inputCls} placeholder={t('newPassword.confirmPlaceholder')} />
              </div>
              {error && <p className="text-[13px] text-rose-600">{error}</p>}
              <button type="submit" disabled={busy} className={`${btnPrimary} h-10 w-full justify-center`}>{busy ? t('newPassword.busy') : t('newPassword.button')}</button>
            </>
          )}
          {done && <p className="text-[13.5px] font-semibold text-emerald-600">{t('newPassword.done')}</p>}
        </form>
      </div>
    </div>
  )
}
